import { ById } from '../services/backendService/types';

/**
 * The lesson metadata keys that travel onto a live game.
 *
 * The api is what normally makes this copy: `GameService.newGame` resolves a
 * lesson's metadata from `templateId` and stamps this same projection onto every
 * game it creates, so the game is self-describing by construction and its readers
 * (the gaps pipeline, and the AiAssessment mirror which cannot afford a DB read)
 * never look the lesson back up.
 *
 * This frontend copy exists for the one case the server cannot cover: a game
 * created BEFORE that stamping landed. `usePresent` forwards it on BOTH launch
 * paths that reuse an existing game — present-from-beginning and resume — where
 * `editGameConfig` MERGES rather than replaces, so an existing game gets healed
 * instead of staying invisible to the dashboard. Resume sends metadata only: its
 * deck may have diverged from the lesson and must not be overwritten.
 *
 * SCALARS ONLY, picked explicitly rather than spread. A game's metadata bag (and
 * the `Template.metadata` DTO) is typed `ById<string | boolean | number>` while a
 * Presentation's permits `string[]` for `passageGenres` — spreading the whole bag
 * would push a list into a scalar-only field. `grade` is the key people forget:
 * without it the grader silently falls back to the grade-5 writing band.
 *
 * Keep this list identical to `GAME_METADATA_KEYS` in the api's
 * `src/shared/utils/lessonGameMetadata.ts`.
 */
const GAME_METADATA_KEYS = [
  'writingPractice',
  'test',
  'testId',
  'questionType',
  'grade',
  'aiGrade',
  'subject',
] as const;

export const lessonGameMetadata = (
  contentMetadata: Record<string, unknown> | null | undefined,
): ById<string | boolean | number> | undefined => {
  if (!contentMetadata) return undefined;
  const picked: ById<string | boolean | number> = {};
  for (const key of GAME_METADATA_KEYS) {
    const value = contentMetadata[key];
    if (
      typeof value === 'string' ||
      typeof value === 'boolean' ||
      typeof value === 'number'
    ) {
      picked[key] = value;
    }
  }
  return Object.keys(picked).length > 0 ? picked : undefined;
};
